import httpClient from '~/lib/http-client';

export interface AnalyticsSummary {
  summary: {
    pageviews: number;
    visitors: number;
    errors: number;
    avgFcp: number | null;
    avgLcp: number | null;
    avgCls: number | null;
    avgPageLoad: number | null;
  };
  devices: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
  topPages: Array<{ path: string; views: number }>;
  topReferrers: Array<{ source: string; visits: number }>;
  topCountries: Array<{ country: string; visits: number }>;
  daily: Array<{ date: string; pageviews: number; visitors: number; errors: number }>;
}

export interface AnalyticsError {
  _id: string;
  /** Not returned by the API — kept optional for legacy callers. */
  siteId?: string;
  message: string;
  stack?: string;
  pageUrl?: string;
  userAgent?: string;
  timestamp: string;
}

const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  BR: 'Brazil',
  MX: 'Mexico',
  IN: 'India',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  RU: 'Russia',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  HK: 'Hong Kong',
  NZ: 'New Zealand',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  IE: 'Ireland',
  PT: 'Portugal',
  ZA: 'South Africa',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PH: 'Philippines',
  ID: 'Indonesia',
  TH: 'Thailand',
  VN: 'Vietnam',
  MY: 'Malaysia',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  EG: 'Egypt',
  NG: 'Nigeria',
  KE: 'Kenya',
  IL: 'Israel',
  TR: 'Turkey',
  UA: 'Ukraine',
  CZ: 'Czech Republic',
  RO: 'Romania',
  HU: 'Hungary',
  GR: 'Greece',
  unknown: 'Unknown',
};

export function getCountryName(code: string): string {
  return COUNTRY_NAMES[code] || code;
}

export async function apiGetAnalytics(deploymentId: string, days = 7): Promise<AnalyticsSummary> {
  const { data } = await httpClient.get<AnalyticsSummary>(`/api/deployments/${deploymentId}/analytics?days=${days}`);
  return data;
}

export async function apiGetStaticAnalytics(deploymentId: string, days = 7): Promise<AnalyticsSummary> {
  const { data } = await httpClient.get<AnalyticsSummary>(
    `/api/deployments/static/${deploymentId}/analytics?days=${days}`,
  );
  return data;
}

export async function apiGetAnalyticsErrors(deploymentId: string, limit = 50): Promise<AnalyticsError[]> {
  const { data } = await httpClient.get<{ errors: AnalyticsError[] }>(
    `/api/deployments/${deploymentId}/analytics/errors?limit=${limit}`,
  );
  return data.errors;
}

export async function apiGetStaticAnalyticsErrors(deploymentId: string, limit = 50): Promise<AnalyticsError[]> {
  const { data } = await httpClient.get<{ errors: AnalyticsError[] }>(
    `/api/deployments/static/${deploymentId}/analytics/errors?limit=${limit}`,
  );
  return data.errors;
}
